import { arrayToMap, countBy, groupAsArrayBy, nonNullable } from 'lib/array/arrayUtils'

export interface AirportTransferPackageOption {
  basePackageView: App.OfferPackageOptionView
  transferPackageView: App.OfferPackageOptionView
  transferInclusion: App.PackageInclusion
  priceDifference: number
}

export interface AirportTransferPackageOptionView extends App.OfferPackageOptionView {
  airportTransferPackageOption?: AirportTransferPackageOption
}

interface AirportTransferRoomPackageOptions {
  collapsedTransferKeys: Set<string>
  pairByBaseKey: Map<string, AirportTransferPackageOption>
}

const TRANSFER_CATEGORY = 'Airport Transfer'
const TRANSFER_DESCRIPTION_PATTERN = /\bairport transfers?\b/i

function normaliseString(value: string | undefined) {
  return value?.trim().replace(/\s+/g, ' ').toLowerCase() ?? ''
}

function getInclusionKey(inclusion: App.PackageInclusion) {
  return [
    normaliseString(inclusion.description),
    inclusion.category ?? '',
    inclusion.parentCategory ?? '',
    inclusion.displayContext ?? '',
    inclusion.minDuration ?? '',
    inclusion.maxDuration ?? '',
  ].join('|')
}

function getInclusionSetKey(inclusions: Array<App.PackageInclusion> | undefined) {
  return (inclusions ?? []).map(getInclusionKey).sort().join('||')
}

function getLuxPlusInclusionsByTierKey(luxPlusInclusionsByTier: App.LuxPlusInclusionsByTier | undefined) {
  return Object.entries(luxPlusInclusionsByTier ?? {})
    .map(([tier, inclusions]) => `${tier}:${getInclusionSetKey(inclusions)}`)
    .sort()
    .join('|')
}

function isAirportTransferInclusion(inclusion: App.PackageInclusion) {
  return inclusion.category === TRANSFER_CATEGORY || TRANSFER_DESCRIPTION_PATTERN.test(inclusion.description)
}

function getPackageInclusions(pkg: App.HotelPackage) {
  return [
    ...(pkg.inclusions ?? []),
    ...(pkg.bonusInclusions ?? []),
  ]
}

function getOnlyAdditionalInclusion(
  basePackage: App.HotelPackage,
  candidateTransferPackage: App.HotelPackage,
): App.PackageInclusion | undefined {
  const baseInclusionKeys = new Set(getPackageInclusions(basePackage).map(getInclusionKey))
  const additionalInclusions = getPackageInclusions(candidateTransferPackage).filter(inclusion => !baseInclusionKeys.has(getInclusionKey(inclusion)))

  if (additionalInclusions.length !== 1) {
    return
  }

  const candidateInclusionKeys = new Set(getPackageInclusions(candidateTransferPackage).map(getInclusionKey))
  const removedInclusions = getPackageInclusions(basePackage).filter(inclusion => !candidateInclusionKeys.has(getInclusionKey(inclusion)))
  if (removedInclusions.length > 0) {
    return
  }

  const [additionalInclusion] = additionalInclusions
  return isAirportTransferInclusion(additionalInclusion) ? additionalInclusion : undefined
}

function buildPackageRulesSignature(view: App.OfferPackageOptionView) {
  const pkg = view.package
  const roomRate = pkg.roomRate

  return JSON.stringify({
    allowBuyNowBookLater: pkg.allowBuyNowBookLater,
    allowDatesRequest: pkg.allowDatesRequest,
    availableRooms: view.availableRooms,
    capacityValid: view.capacityValid,
    cancellationPolicies: roomRate.cancellationPolicies,
    cancellationPolicy: roomRate.cancellationPolicy,
    duration: pkg.duration,
    flightsAvailable: view.flightsAvailable,
    fromCalendarInfo: view.fromCalendarInfo,
    hotelAvailable: view.hotelAvailable,
    ignoreDynamicCancellationPolicy: pkg.ignoreDynamicCancellationPolicy,
    includedGuests: roomRate.includedGuests,
    isReservableForZero: roomRate.isReservableForZero,
    luxPlusInclusionsByTier: getLuxPlusInclusionsByTierKey(pkg.luxPlusInclusionsByTier),
    hasMemberPricing: getMemberPackageViewPrice(view) > 0,
    roomRateCapacities: roomRate.capacities,
    roomRateSchedules: roomRate.schedules,
    packageSchedules: pkg.schedules,
    travelPeriodFrom: pkg.travelPeriodFrom,
    travelPeriodTo: pkg.travelPeriodTo,
  })
}

function canComparePackageViews(baseView: App.OfferPackageOptionView, transferView: App.OfferPackageOptionView) {
  return baseView.package.roomType?.id === transferView.package.roomType?.id &&
    baseView.package.duration === transferView.package.duration &&
    baseView.package.roomRate?.isPackaged === transferView.package.roomRate?.isPackaged &&
    baseView.hasAvailableRates === transferView.hasAvailableRates &&
    buildPackageRulesSignature(baseView) === buildPackageRulesSignature(transferView)
}

function getPackageViewPrice(view: App.OfferPackageOptionView) {
  return view.pricing?.price ?? view.hotelTotal
}

function getMemberPackageViewPrice(view: App.OfferPackageOptionView) {
  return view.memberPricing?.price ?? view.hotelMemberTotal
}

function getPackagePriceDifference(
  baseView: App.OfferPackageOptionView,
  transferPackageView: App.OfferPackageOptionView,
) {
  const baseMemberPrice = getMemberPackageViewPrice(baseView)
  const transferMemberPrice = getMemberPackageViewPrice(transferPackageView)
  const shouldUseMemberPrice = baseMemberPrice > 0 && transferMemberPrice > 0
  const basePrice = shouldUseMemberPrice ? baseMemberPrice : getPackageViewPrice(baseView)
  const transferPrice = shouldUseMemberPrice ? transferMemberPrice : getPackageViewPrice(transferPackageView)

  return Math.max(0, transferPrice - basePrice)
}

function findTransferPackagePair(
  baseView: App.OfferPackageOptionView,
  views: Array<App.OfferPackageOptionView>,
): AirportTransferPackageOption | undefined {
  const matches = views.filter(candidateView => {
    if (candidateView.package.uniqueKey === baseView.package.uniqueKey || !canComparePackageViews(baseView, candidateView)) {
      return false
    }

    return !!getOnlyAdditionalInclusion(baseView.package, candidateView.package)
  })

  if (matches.length !== 1) {
    return
  }

  const [transferPackageView] = matches
  const transferInclusion = getOnlyAdditionalInclusion(baseView.package, transferPackageView.package)
  if (!transferInclusion) {
    return
  }

  const priceDifference = getPackagePriceDifference(baseView, transferPackageView)

  return {
    basePackageView: baseView,
    transferPackageView,
    transferInclusion,
    priceDifference,
  }
}

function buildAirportTransferRoomPackageOptions(
  views: Array<App.OfferPackageOptionView>,
): AirportTransferRoomPackageOptions {
  const possiblePairs = nonNullable(views.map(view => findTransferPackagePair(view, views)))
  const transferPackagePairCounts = countBy(possiblePairs, pair => pair.transferPackageView.package.uniqueKey)
  const pairs = possiblePairs.filter(pair => transferPackagePairCounts.get(pair.transferPackageView.package.uniqueKey) === 1)

  return {
    collapsedTransferKeys: new Set(pairs.map(pair => pair.transferPackageView.package.uniqueKey)),
    pairByBaseKey: arrayToMap(pairs, pair => pair.basePackageView.package.uniqueKey),
  }
}

function getAirportTransferPackageOptionViewsForRoom(
  views: Array<App.OfferPackageOptionView>,
): Array<AirportTransferPackageOptionView> {
  const { collapsedTransferKeys, pairByBaseKey } = buildAirportTransferRoomPackageOptions(views)

  return views
    .filter(view => !collapsedTransferKeys.has(view.package.uniqueKey))
    .map(view => {
      const pair = pairByBaseKey.get(view.package.uniqueKey)
      return pair ? { ...view, airportTransferPackageOption: pair } : view
    })
}

export function groupAirportTransferPackageOptions(
  packageViews: Array<App.OfferPackageOptionView>,
): Array<AirportTransferPackageOptionView> {
  return groupAsArrayBy(packageViews, view => view.package.roomType?.id ?? '')
    .flatMap(([, views]) => getAirportTransferPackageOptionViewsForRoom(views))
}
